import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { toast } from 'sonner'

import { skidsService } from '@/api/skids/skids-service'
import { resolveSkidLengthBump } from '@/utils/order-skids'

/**
 * Grows a skid's overall packaged length when a longer part lands on it. The pallet stays
 * 10 ft — the part just hangs over the end — and the backend never raises `length` itself.
 *
 * Shared by every assign path (skid sheet bulk scan, bundle→skid scan sheet, inline
 * Ship. Unit cell) for one reason beyond reuse: the caller's `length` comes from a query
 * cache that is still stale during a burst of scans. Remembering what we already PATCHed
 * per skid keeps a shorter follow-up part from lowering the length again.
 *
 * Best-effort by design: the bundle is already assigned when this runs, so a failed PATCH
 * must not read as a failed scan.
 */
/**
 * Module scope, not a ref: the inline Ship. Unit cell mounts one hook per bundle row, so
 * a per-instance memory would forget the bump the previous row just applied and let the
 * next, shorter part lower the length again.
 */
const appliedLengths = new Map<number, number>()

/**
 * Call after a deliberate resize: the remembered bump belongs to the old size and would
 * otherwise push a fresh 4x4 past its fixed 48.
 */
export const forgetSkidLengthBump = (skidId: number) => {
  appliedLengths.delete(skidId)
}

export const useSkidLengthBump = () => {
  const queryClient = useQueryClient()

  return useCallback(
    (
      skidId: number | null | undefined,
      knownLength: number | null | undefined,
      detailLength: number | null | undefined
    ) => {
      if (skidId == null) return
      const baseline = Math.max(knownLength ?? 120, appliedLengths.get(skidId) ?? 0)
      const nextLength = resolveSkidLengthBump(baseline, detailLength)
      if (nextLength == null) return

      appliedLengths.set(skidId, nextLength)
      skidsService
        .update(skidId, { length: nextLength })
        .then(() => queryClient.invalidateQueries({ queryKey: ['skids'] }))
        .catch(() => {
          // Let the next scan retry the same bump.
          appliedLengths.delete(skidId)
          toast.warning('Bundle assigned, but the skid length was not updated')
        })
    },
    [queryClient]
  )
}
